/**
 * The only place in the app permitted to convert a money string to a number, and only as a guarded
 * fallback for engines without `Intl.NumberFormat`'s string overload (iOS Safari < 15.4).
 *
 * Amounts arrive as decimal strings in major units (`"2500.00"`) and stay strings. `parseFloat` on an
 * amount anywhere else is a review failure: the value is displayed to a player about to send money,
 * and float rounding on money is a category error even when it happens to round correctly.
 */

const DECIMAL_STRING = /^-?\d+(\.\d+)?$/;

export function isValidAmountString(amount: unknown): amount is string {
  return typeof amount === 'string' && DECIMAL_STRING.test(amount);
}

/**
 * Clamp the API-provided currency precision to the backend contract. The value is a display
 * preference only; the original API string remains untouched everywhere else.
 */
function clampDecimalPlaces(decimalPlaces: number): number {
  if (!Number.isFinite(decimalPlaces)) return 2;
  return Math.min(8, Math.max(0, Math.trunc(decimalPlaces)));
}

function truncateAndPad(amount: string, decimalPlaces: number): string {
  const negative = amount.startsWith('-');
  const absolute = negative ? amount.slice(1) : amount;
  const [wholePart = '0', fractionPart = ''] = absolute.split('.');
  const whole = wholePart.replace(/^0+(?=\d)/, '') || '0';
  if (decimalPlaces === 0) return `${negative ? '-' : ''}${whole}`;
  return `${negative ? '-' : ''}${whole}.${fractionPart.slice(0, decimalPlaces).padEnd(decimalPlaces, '0')}`;
}

/**
 * `currencyDisplay: 'code'` so the player sees `PKR 2,500.00` rather than a symbol. Symbols are
 * ambiguous across locales — `$` is at least six different currencies — and on a payment screen the
 * unambiguous form is worth the extra characters.
 *
 * An invalid string is returned unchanged: `2500.00` unformatted is strictly better than `NaN`.
 */
export function formatMoney(
  amount: string,
  currency: string,
  locale: string,
  decimalPlaces = 2,
): string {
  if (!isValidAmountString(amount)) return amount;

  const fractionDigits = clampDecimalPlaces(decimalPlaces);
  const displayAmount = truncateAndPad(amount, fractionDigits);

  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'code',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });

    // The string overload keeps full decimal precision and is the reason this helper exists. It is
    // absent before iOS Safari 15.4, which is inside our support floor, hence the try/catch below.
    return formatter.format(displayAmount as unknown as number);
  } catch {
    return formatWithoutStringOverload(displayAmount, currency, locale, fractionDigits);
  }
}

function formatWithoutStringOverload(
  amount: string,
  currency: string,
  locale: string,
  fractionDigits: number,
): string {
  try {
    const asNumber = Number(amount);
    if (!Number.isFinite(asNumber)) return `${currency} ${amount}`;
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'code',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(asNumber);
  } catch {
    // An unknown or malformed currency code lands here. Showing the code beside the raw amount keeps
    // both pieces of information the player needs.
    return `${currency} ${amount}`;
  }
}

/**
 * String equality, not numeric comparison.
 *
 * `"100.0"` and `"100.00"` are different strings and both should surface for staff review — the
 * backend chose to send a different representation, and hiding that is a decision we are not
 * entitled to make. It is also cheaper than parsing, and cannot round.
 */
export function amountsDiffer(amount: string, receivedAmount: string | null): boolean {
  return receivedAmount !== null && receivedAmount !== amount;
}
